import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import Text, {
  TEXT_ALIGN,
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_SEGMENT,
  TEXT_SIZE,
} from "@/bit/components/components.text";
import CustomLink from "@/bit/components/components.custom-link";
import Icon from "@/bit/components/components.icon-glyph";
import getDeviceKindDependsOnViewPort from "@gcloud-npm/utils.common.get-device-kind-depends-on-viewport";
import TextBlockItem from "../text-block-item";

export const COMPONENT_NAME = "TextBlock";
export { TEXT_COLOR, TEXT_SEGMENT };

const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;

/**
 * Componente que permite crear una lista desplegable de elementos.
 */
const TextBlock = (props) => {
  const {
    defaultExpanded,
    cmsHandler,
    blockName,
    alias,
    maxLines,
    maxLinesMobile,
    hideExpand,
    withBorder,
    CmsAddElement,
    CmsEditableGroupIcons,
  } = props;
  const itemsList = props["cms-ml-single-text-block-list"];
  const [finalMaxLines, setFinalMaxLines] = useState(4);
  const [showAll, setShowAll] = useState(
    defaultExpanded === "true" || hideExpand === "true" || hideExpand === true,
  );
  const isFirstRender = useRef(true);
  const containerRef = useRef(null);

  const taggingInfo = {
    componentName: COMPONENT_NAME,
    componentContent: "",
    componentProduct: "",
    componentVariation: "none",
    eventType: "click",
  };

  useEffect(() => {
    setFinalMaxLines(
      getDeviceKindDependsOnViewPort() === "MOBILE" ? maxLinesMobile : maxLines,
    );
    updateLinkFocusability();
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [showAll]);

  const toggleShowAll = () => {
    setShowAll((value) => !value);
  };

  const focusFirstVisibleFocusableElement = () => {
    const firstFocusable = containerRef.current.querySelector("a");
    if (firstFocusable && firstFocusable.tabIndex !== -1) {
      firstFocusable.focus();
    }
  };

  //Mejora de accesibilidad para que solo sean focusables si están en un area visible
  const updateLinkFocusability = () => {
    const container = containerRef.current;
    if (container) {
      const links = container.querySelectorAll("a");
      const containerRect = container.getBoundingClientRect();

      links.forEach((link) => {
        const linkRect = link.getBoundingClientRect();
        const isVisible =
          linkRect.top >= containerRect.top &&
          linkRect.bottom <= containerRect.bottom;
        link.tabIndex = isVisible ? 0 : -1;
      });
      if (!isFirstRender.current) {
        focusFirstVisibleFocusableElement();
      }
    }
  };

  // Cuando no hay elementos
  if (
    (currentEnv === "qa_ssr" || currentEnv === "local") &&
    itemsList &&
    Array.isArray(itemsList) &&
    itemsList.length === 0
  ) {
    return (
      <section
        id={blockName || ""}
        className={`text-block container${withBorder === true || withBorder === "true" ? " with-border" : ""}`}
      >
        <div className={`text-block-item`}>
          <Text text="Placeholder TextBlock" />
        </div>
        {CmsAddElement && (
          <CmsAddElement
            addProps={{
              index: "start",
              itemIdDotNotation: `${blockName}.cms-ml-single-text-block-list`,
              contentId: blockName,
              small: true,
              layout: "vertical",
            }}
            cmsHandler={cmsHandler}
          />
        )}
      </section>
    );
  }

  return (
    <>
      {/* Bolitas de edicion aqui */}
      {CmsEditableGroupIcons && (
        <CmsEditableGroupIcons
          elementProps={{
            ...props,
            editName: "cms-block.text-block",
          }}
          deleteProps={{}}
          visibleTools={{
            hiring: false,
            edit: true,
            deleteItem: false,
          }}
          cmsHandler={cmsHandler}
        />
      )}
      {Array.isArray(itemsList) && itemsList.length > 0 && (
        <section
          id={blockName || ""}
          className={`text-block container${withBorder === true || withBorder === "true" ? " with-border" : ""}`}
        >
          <div
            className={`text-block-items ${showAll ? "" : "collapse"}`}
            ref={containerRef}
            style={{
              lineClamp: finalMaxLines || 4,
              WebkitLineClamp: finalMaxLines || 4,
            }}
          >
            {CmsAddElement && (
              <CmsAddElement
                addProps={{
                  index: "start",
                  contentId: blockName,
                  itemIdDotNotation: `${blockName}.cms-ml-single-text-block-list`,
                  small: true,
                  layout: "vertical",
                }}
                cmsHandler={cmsHandler}
              />
            )}
            {itemsList.map((item, index) => (
              <TextBlockItem
                {...item}
                key={`text-item--${index}`}
                blockName={blockName}
                alias={alias}
                index={index}
                frontalListToDelete={itemsList}
                cmsHandler={cmsHandler}
                CmsEditableGroupIcons={CmsEditableGroupIcons}
              />
            ))}
            {CmsAddElement && (
              <CmsAddElement
                addProps={{
                  index: "end",
                  contentId: blockName,
                  itemIdDotNotation: `${blockName}.cms-ml-single-text-block-list`,
                  small: true,
                  layout: "vertical",
                }}
                cmsHandler={cmsHandler}
              />
            )}
          </div>

          {(!hideExpand || hideExpand === "false") && (
            <CustomLink
              id="toggle-button"
              title={showAll ? "Ver menos" : "Ver más"}
              ariaLabel={showAll ? "Ver menos" : "Ver más"}
              taggingInfo={{
                ...taggingInfo,
                componentProduct: `listItems`,
                componentContent: showAll ? "list-plegar" : "list-desplegar",
                componentVariation: "none",
                eventType: "click",
              }}
              className={`toggle-button ${showAll ? "open" : "close"}`}
              onClick={toggleShowAll}
            >
              <Text
                text={showAll ? "Ver menos" : "Ver más"}
                align={TEXT_ALIGN.LEFT}
                color={TEXT_COLOR.BLUEA}
                size={TEXT_SIZE.SUBTITLE2}
                fontType={TEXT_FONT_WEIGHT.REGULAR}
                className="text-toggle"
              />
              <span className="icon">
                <Icon id="cheuron-up-m" color="blue_a" width={12} />
              </span>
            </CustomLink>
          )}
        </section>
      )}
      <style jsx>{`.text-block {
  background-color: #ffffff;
  margin-bottom: 1rem;
  &.with-border {
    border: 1px solid #eeeeee;
    border-radius: 16px;
    opacity: 1;
    padding: 1rem;
  }
  :global(.text-block-item) {
    margin: 0.5rem 0.2rem;
    position: relative;
  }
  :global(.toggle-button) {
    display: flex;
    align-items: baseline;
    gap: 4px;
    .icon {
      display: flex;
      margin: auto;
      transform: rotate(180deg);
      -webkit-transition: all 0.3s ease-out;
      -moz-transition: all 0.3s ease-out;
      -o-transition: all 0.3s ease-out;
      transition: all 0.3s ease-out;
    }
  }
  :global(.open) {
    .icon {
      display: flex;
      margin: auto;
      transform: rotate(0deg);
      -webkit-transition: all 0.3s ease-out;
      -moz-transition: all 0.3s ease-out;
      -o-transition: all 0.3s ease-out;
      transition: all 0.3s ease-out;
    }
  }
  .text-toggle {
    margin: 0.5rem 0.2rem;
  }

  .text-block-items.collapse {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 1rem;
  }

  .item-description {
    :global(span p) {
      margin-bottom: 0.5rem;
    }
    @media (max-width: 480px) {
      :global(span p:nth-last-child(1)) {
        margin-bottom: 1rem;
      }
    }
  }

  :global(.item-title) {
    margin-bottom: 0.5rem;
  }
}
`}</style>
    </>
  );
};

TextBlock.propTypes = {
  /**
   * {
   *  "info": "A partir de cuantas lineas se reduce el texto",
   *  "kind": "both",
   *  "beautifulName": "Máximo de líneas antes de resumir"
   * }
   */
  maxLines: PropTypes.number,
  /**
   * {
   *  "info": "A partir de cuantas lineas se reduce el texto EN MOVIL",
   *  "kind": "both",
   *  "beautifulName": "Máximo de líneas antes de resumir EN MOVIL"
   * }
   */
  maxLinesMobile: PropTypes.number,
  /**
   * {
   *  "info": "Ocultar botón Ver más y por defecto siempre expandido",
   *  "kind": "both",
   *  "beautifulName": "Desactivar funcionalidad de collapsible list"
   * }
   */
  hideExpand: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * {
   *  "info": "Exponer todo el texto por defecto (sin necesidad de hacer click en ver más)",
   *  "kind": "both",
   *  "beautifulName": "Expandido por defecto"
   * }
   */
  defaultExpanded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * {
   *  "info": "Borde que rodea el bloque de texto",
   *  "kind": "both",
   *  "beautifulName": "Borde rodeando el texto"
   * }
   */
  withBorder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

TextBlock.defaultProps = {
  maxLines: 4,
  maxLinesMobile: 10,
  hideExpand: false,
  defaultExpanded: false,
  withBorder: false,
};

export default TextBlock;
